import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePage } from "@inertiajs/react";

import ContactModal from "/components/Projects/ContactModal";
import ContactButton from "/components/Projects/ContactButton";
import Button from "/components/Button";
import ScrollUpButton from "/components/ScrollUpButton";
import ProjectsFilter from "./Filter";
import ProjectCard from "./Card";
import ProjectSimpleCard from "./SimpleCard";
import { buildURLWithQueryString } from "/utils/helpers";
import { useHeaderHeight } from "/utils/header-height-context";

export default function ProjectsList({
  sourceURL,
}) {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { t } = useTranslation();
  const {
    projects,
    total,
    per_page,
    locale,
    dictionaries,
    project_type_options,
    client_options,
    layout_content_type_options,
    design_system_element_options,
    featured_project_type_ids = [],
    featured_client_ids = [],
    featured_layout_content_type_ids = [],
    featured_design_system_element_ids = [],
    is_mobile,
    project_type_ids = [],
    client_ids = [],
    layout_content_type_ids = [],
    design_system_element_ids = [],
  } = usePage().props;

  const headerHeight = useHeaderHeight();
  const [memTotal, setMemTotal] = useState(total);
  const [memProjects, setMemProjects] = useState(projects);
  const [currentPage, setCurrentPage] = useState(1);
  const [projectTypeFilters, setProjectTypeFilters] = useState(project_type_ids);
  const [clientFilters, setClientFilters] = useState(client_ids);
  const [layoutContentTypeFilters, setLayoutContentTypeFilters] = useState(layout_content_type_ids);
  const [designSystemElementFilters, setDesignSystemElementFilters] = useState(design_system_element_ids);
  const firstRender = useRef(true);
  const projectsLeft = memTotal - memProjects.length;
  const loadMoreCount = Math.min(per_page, projectsLeft);

  const loadMore = () => {
    const nextPage = currentPage + 1;
    const queryParams = {
      project_type_ids: projectTypeFilters,
      client_ids: clientFilters,
      layout_content_type_ids: layoutContentTypeFilters,
      design_system_element_ids: designSystemElementFilters,
      page: nextPage,
    };
    const fetchURL = buildURLWithQueryString(sourceURL, queryParams);

    setCurrentPage(nextPage);

    fetch(
      fetchURL,
      {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      }
    ).then(response =>
      response.json()
    ).then(data => {
      setMemProjects(currentProjects => [...currentProjects, ...data.projects]);
    });
  };

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    const queryParams = {
      project_type_ids: projectTypeFilters,
      client_ids: clientFilters,
      layout_content_type_ids: layoutContentTypeFilters,
      design_system_element_ids: designSystemElementFilters,
    };
    const fetchURL = buildURLWithQueryString(sourceURL, queryParams);

    fetch(
      fetchURL,
      {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
      }
    ).then(response =>
      response.json()
    ).then(data => {
      setMemProjects(data.projects);
      setMemTotal(data.total);
      setCurrentPage(1); // the filtered list starts over at the first page
    });
  }, [projectTypeFilters, clientFilters, layoutContentTypeFilters, designSystemElementFilters]);

  return (
    <>
      <ContactModal isOpen={isContactModalOpen} setIsOpen={setIsContactModalOpen} />
      <ContactButton setIsModalOpen={setIsContactModalOpen} mobile={true} />

      <div className="projects-container">
        <div className="projects-filters-wrapper">
          <div className="projects-filters" style={{ top: `calc(${headerHeight}px - var(--header-offset, 0px) + var(--projects-filters-gap))` }}>
            <ContactButton setIsModalOpen={setIsContactModalOpen} />

            <ProjectsFilter
              label={dictionaries.project_types[`name_${locale}`]}
              options={project_type_options}
              featuredOptions={featured_project_type_ids}
              selectedOptions={projectTypeFilters}
              onChange={setProjectTypeFilters}
            />

            <ProjectsFilter
              label={dictionaries.clients[`name_${locale}`]}
              options={client_options}
              featuredOptions={featured_client_ids}
              selectedOptions={clientFilters}
              onChange={setClientFilters}
            />

            {/* The two layout-level dictionaries are desktop-only: the mobile
                filter bar keeps to the project's own attributes. */}
            {!is_mobile && (
              <>
                <ProjectsFilter
                  label={dictionaries.layout_content_types[`name_${locale}`]}
                  options={layout_content_type_options}
                  featuredOptions={featured_layout_content_type_ids}
                  selectedOptions={layoutContentTypeFilters}
                  onChange={setLayoutContentTypeFilters}
                />

                <ProjectsFilter
                  label={dictionaries.design_system_elements[`name_${locale}`]}
                  options={design_system_element_options}
                  featuredOptions={featured_design_system_element_ids}
                  selectedOptions={designSystemElementFilters}
                  onChange={setDesignSystemElementFilters}
                />
              </>
            )}

            <ScrollUpButton />
          </div>
        </div>

        <div className="flex-col full-width gap-18">
          {memProjects.map(project => is_mobile ? (
            <ProjectSimpleCard key={project.id} project={project} />
          ) : (
            <ProjectCard key={project.id} project={project} />
          ))}

          {loadMoreCount > 0 && (
            <div className="load-more-wrapper">
              <Button onClick={loadMore}>{t("projects.loadMore", { count: loadMoreCount, left: projectsLeft })}</Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
